/**
 * AssistantSecurityPanel — Authenticator-Einrichtung (TOTP) + bestätigungspflichtige Aktionen + Drive-Abgleich.
 *
 * SICHERHEIT: Jede Schreib-Aktion erfordert einen 6-stelligen Code aus der Authenticator-App.
 * Drive-Abgleich ist nur lesend. Nichts wird gelöscht.
 */

import { useEffect, useState } from 'react';
import { X, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import QRCode from 'qrcode';
import { supabase } from '@/lib/supabase';

async function apiPost(path: string, body: any) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => ({}));
}

type AktionsTyp = 'aufgabe_anlegen' | 'dokument_status_setzen' | 'entwicklungsauftrag_anlegen';

export default function AssistantSecurityPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const [aktion, setAktion] = useState<AktionsTyp>('aufgabe_anlegen');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [dokId, setDokId] = useState('');
  const [dokStatus, setDokStatus] = useState('geprueft');
  const [code, setCode] = useState('');
  const [ergebnis, setErgebnis] = useState<string | null>(null);

  const [driveFolder, setDriveFolder] = useState('');
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveResult, setDriveResult] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null); setErgebnis(null);
    apiPost('/api/assistant-totp', { action: 'status' }).then((d) => setEnrolled(!!d?.enrolled));
  }, [open]);

  async function starteSetup() {
    setBusy(true); setMsg(null);
    const d = await apiPost('/api/assistant-totp', { action: 'setup' });
    if (d?.otpauth_url) {
      setSecret(d.secret_base32);
      try { setQr(await QRCode.toDataURL(d.otpauth_url, { margin: 1, width: 220 })); } catch { setQr(null); }
    } else setMsg(d?.error || 'Einrichtung fehlgeschlagen.');
    setBusy(false);
  }

  async function bestaetigeSetup() {
    setBusy(true); setMsg(null);
    const d = await apiPost('/api/assistant-totp', { action: 'confirm', code: setupCode });
    if (d?.ok) { setEnrolled(true); setQr(null); setSecret(null); setSetupCode(''); setMsg('✓ Authenticator eingerichtet.'); }
    else setMsg(d?.error || 'Code ungültig.');
    setBusy(false);
  }

  async function fuehreAus() {
    setBusy(true); setErgebnis(null);
    const params: any = {};
    if (aktion === 'aufgabe_anlegen') { params.titel = titel; params.beschreibung = beschreibung; }
    if (aktion === 'entwicklungsauftrag_anlegen') { params.titel = titel; params.beschreibung = beschreibung; }
    if (aktion === 'dokument_status_setzen') { params.dokument_id = dokId; params.status = dokStatus; }
    const d = await apiPost('/api/assistant-action', { action: aktion, code, params });
    setErgebnis(d?.ergebnis || d?.error || 'Unbekanntes Ergebnis.');
    if (d?.ok) { setTitel(''); setBeschreibung(''); setDokId(''); setCode(''); }
    setBusy(false);
  }

  async function pruefeDrive() {
    if (!driveFolder.trim() || driveBusy) return;
    setDriveBusy(true); setDriveResult(null);
    const d = await apiPost('/api/drive-abgleich', { folder_id: driveFolder.trim() });
    setDriveResult(d);
    setDriveBusy(false);
  }

  if (!open) return null;

  const inputCls = 'w-full bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-slate-100 placeholder:text-slate-500 outline-none';

  return (
    <div className="md:hidden fixed inset-0 z-[110] flex flex-col bg-gradient-to-b from-[#0e1430] via-[#0a0f22] to-[#070a16] text-slate-100">
      <div className="flex items-center gap-3 px-4 pt-[calc(env(safe-area-inset-top)+14px)] pb-3 border-b border-white/10">
        <ShieldCheck className="w-6 h-6 text-emerald-400" />
        <p className="flex-1 text-sm font-semibold">Sicherheit & Aktionen</p>
        <button onClick={onClose} aria-label="Schließen" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {enrolled === null && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>}

        {enrolled === false && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
            <p className="text-sm font-semibold">Authenticator einrichten</p>
            <p className="text-[12px] text-slate-400">Jede Änderung (Aufgabe, Status, Auftrag) wird mit einem Code aus deiner Authenticator-App bestätigt. Einmalig einrichten:</p>
            {!qr && <button disabled={busy} onClick={starteSetup} className="px-4 py-2 rounded-lg bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white text-[13px] disabled:opacity-50">QR-Code erzeugen</button>}
            {qr && (
              <div className="space-y-3">
                <img src={qr} alt="QR" className="rounded-lg bg-white p-2 w-[220px] max-w-full mx-auto" />
                <p className="text-[11px] text-slate-400 break-all">Alternativ manuell: <span className="text-slate-200">{secret}</span></p>
                <p className="text-[12px] text-slate-300">Scanne den Code mit Google/Microsoft Authenticator und gib zur Bestätigung den aktuellen 6-stelligen Code ein:</p>
                <input value={setupCode} onChange={(e) => setSetupCode(e.target.value)} inputMode="numeric" placeholder="123456" className={inputCls} />
                <button disabled={busy} onClick={bestaetigeSetup} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-[13px] disabled:opacity-50">Bestätigen</button>
              </div>
            )}
            {msg && <p className="text-[12px] text-amber-300">{msg}</p>}
          </div>
        )}

        {enrolled === true && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 text-[12px]"><CheckCircle2 className="w-4 h-4" /> Authenticator aktiv</div>

            <div className="flex gap-2 flex-wrap">
              {([['aufgabe_anlegen', 'Aufgabe'], ['dokument_status_setzen', 'Dok-Status'], ['entwicklungsauftrag_anlegen', 'Dev-Auftrag']] as [AktionsTyp, string][]).map(([id, label]) => (
                <button key={id} onClick={() => { setAktion(id); setErgebnis(null); }} className={`text-[12px] px-3 py-1.5 rounded-full border ${aktion === id ? 'bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white border-transparent' : 'border-white/15 bg-white/5 text-slate-300'}`}>{label}</button>
              ))}
            </div>

            {(aktion === 'aufgabe_anlegen' || aktion === 'entwicklungsauftrag_anlegen') && (
              <>
                <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Titel" className={inputCls} />
                <textarea value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} placeholder="Beschreibung (optional)" rows={3} className={inputCls} />
              </>
            )}
            {aktion === 'dokument_status_setzen' && (
              <>
                <input value={dokId} onChange={(e) => setDokId(e.target.value)} placeholder="Dokument-ID" className={inputCls} />
                <select value={dokStatus} onChange={(e) => setDokStatus(e.target.value)} className={inputCls}>
                  {['offen', 'in_pruefung', 'geprueft', 'freigegeben', 'abgelehnt'].map((s) => <option key={s} value={s} className="bg-[#0e1430]">{s}</option>)}
                </select>
              </>
            )}

            <div className="pt-1 border-t border-white/10" />
            <p className="text-[12px] text-slate-400">Bestätige mit dem aktuellen Code aus deiner Authenticator-App:</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="6-stelliger Code" className={inputCls} />
            <button disabled={busy} onClick={fuehreAus} className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white text-[13px] font-medium disabled:opacity-50 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Bestätigen & ausführen
            </button>
            {ergebnis && <p className="text-[12px] text-slate-200 bg-white/5 rounded-lg p-3">{ergebnis}</p>}
          </div>
        )}

        {/* Drive-Abgleich: welche Drive-Dateien fehlen im System? (nur lesend) */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
          <p className="text-sm font-semibold">Drive-Abgleich</p>
          <p className="text-[12px] text-slate-400">Prüft, welche Dateien in einem Google-Drive-Ordner noch nicht im System sind (nur lesend, importiert nichts). Der Ordner muss mit dem App-Service-Account geteilt sein.</p>
          <input value={driveFolder} onChange={(e) => setDriveFolder(e.target.value)} placeholder="Google-Drive-Ordner-ID" className={inputCls} />
          <button disabled={driveBusy} onClick={pruefeDrive} className="px-4 py-2 rounded-lg bg-gradient-to-br from-[#6d8bff] to-[#9d6bff] text-white text-[13px] disabled:opacity-50 flex items-center justify-center gap-2">
            {driveBusy && <Loader2 className="w-4 h-4 animate-spin" />} Abgleichen
          </button>
          {driveResult && (
            <div className="text-[12px] text-slate-200 bg-white/5 rounded-lg p-3 space-y-1">
              {driveResult.error ? (
                <p className="text-amber-300">{driveResult.error}</p>
              ) : driveResult.hinweis ? (
                <p className="text-amber-300">{driveResult.hinweis}</p>
              ) : (
                <>
                  <p>Drive-Dateien gescannt: <b>{driveResult.drive_gesamt}</b>{driveResult.limitiert ? ' (Limit erreicht)' : ''}</p>
                  <p>Im System vorhanden: <b className="text-emerald-400">{driveResult.im_system}</b></p>
                  <p>Fehlend im System: <b className="text-amber-300">{driveResult.fehlend}</b></p>
                  {Array.isArray(driveResult.fehlend_beispiele) && driveResult.fehlend_beispiele.length > 0 && (
                    <div className="pt-1 text-slate-400">
                      Beispiele:
                      <ul className="list-disc ml-4 mt-1 space-y-0.5">
                        {driveResult.fehlend_beispiele.slice(0, 10).map((f: any) => <li key={f.id} className="truncate">{f.name}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-500">Es gibt keine Lösch-Funktion. Alle Aktionen werden protokolliert. Entwicklungs-Aufträge gehen erst nach deinem Merge live.</p>
      </div>
    </div>
  );
}
